/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import React, { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import PaginationInfo from '../../../../client/js/components/pagination/PaginationInfo'
import PaginationNav from '../../../../client/js/components/pagination/PaginationNav'
import PaginationProvider from '../../../../client/js/components/pagination/PaginationProvider'
import usePagination from '../../../../client/js/hooks/usePagination'
import { findManyInterventions } from '../../lib/rest/interventionAPIClient'
import { getInterventionOptionLabel } from '../inputs/InterventionInput'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { Box, Divider, Group, Modal, ModalProps, NavLink, Stack, TextInput } from '@mantine/core'
import NoData from '../../../../client/js/components/NoData'

export type InterventionSelectorModalProps = ModalProps & {
  queryFilter: Record<string, string>,
  onClickResult (item: Intervention): void,
}

function InterventionSelectorModal (props: InterventionSelectorModalProps) {
  const {
    onClickResult,
    onClose,
    queryFilter,
    ...others
  } = props

  const intl = useIntl()
  const [search, setSearch] = useState<string>(null)
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePagination({
    page,
    limit,
    totalElements,
    onChange (page: number, limit: number) {
      setPage(page)
      setLimit(limit)
    }
  })

  const loader = useQuery({
    enabled: true,
    queryKey: ['interventions', queryFilter, search, page, limit],
    queryFn: () => findManyInterventions({
      ...queryFilter,
      search,
      page: pagination.page,
      limit: pagination.limit
    }).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (loader.data != null ? getHalEmbedded(loader.data)?.interventions : null) || []
  ), [loader.data])

  const handleClickResult = useCallback((item: Intervention) => () => {
    onClickResult(item)
  }, [onClickResult])

  const handleSearchChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(ev.target.value)
    setPage(1)
  }, [setPage])

  return (
    <Modal
      onClose={onClose}
      title={intl.formatMessage({
        id: 'selection_of_intervention',
        defaultMessage: 'Sélection d\'une intervention'
      })}
      {...others}
    >
      <Stack>
        <TextInput
          name="interventionId"
          onChange={handleSearchChange}
          placeholder={intl.formatMessage({
            id: 'search',
            defaultMessage: 'Rechercher'
          })}
        />
        <Divider />
        <PaginationProvider context={pagination}>
          <LoadingHelper
            query={loader}
            render={() => (
              <Box>
                {items.length > 0
                  ? items.map((item) => (
                    <NavLink
                      key={item.id}
                      onClick={handleClickResult(item)}
                      label={getInterventionOptionLabel(item)}
                    />
                  ))
                  : <NoData />
                }
              </Box>
            )}
          />
          <Group>
            <PaginationNav />
            <PaginationInfo />
          </Group>
        </PaginationProvider>
      </Stack>
    </Modal>
  )
}

export default InterventionSelectorModal
